import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ValidationErrors, Validators } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { connectState } from '@examdojo/angular/util';
import { SyllabusQuery } from '@examdojo/category';
import { IconComponent } from '@examdojo/core/icon';
import { JsonEditorComponent } from '@examdojo/core/json-editor';
import { QuestionItemV2 } from '@examdojo/models/question';
import { QuestionItemsJsonEditorDirective, QuestionItemsPreviewComponent } from '@examdojo/question';
import { editor as monacoEditor } from 'monaco-editor';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';

@Component({
  selector: 'exam-preview-from-json',
  templateUrl: './preview-from-json.component.html',
  styleUrls: ['./preview-from-json.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'h-full' },
  imports: [
    JsonEditorComponent,
    QuestionItemsJsonEditorDirective,
    MatProgressSpinnerModule,
    IconComponent,
    QuestionItemsPreviewComponent,
  ],
})
export class PreviewFromJsonComponent {
  constructor(private readonly syllabusQuery: SyllabusQuery) {
    this.control.valueChanges
      .pipe(
        tap(() => this.validationInProgress.set(true)),
        debounceTime(600),
        distinctUntilChanged(),
        tap((v) => {
          this.controlErrors.set(this.parseControlErrors(this.control.errors));

          if (this.control.errors?.['monacoValidation'] || this.control.errors?.['invalidJson']) {
            return;
          }

          try {
            const updatedQuestionItems = JSON.parse(v || '[]') as QuestionItemV2[];

            this.questionItems.set(updatedQuestionItems);
          } catch (error) {
            console.error('Invalid JSON', error);
          }
        }),
        tap(() => this.validationInProgress.set(false)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  readonly questionItems = signal<QuestionItemV2[]>([]);
  readonly controlErrors = signal<string[] | null>(null);

  readonly validationInProgress = signal<boolean>(false);

  readonly control = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required],
  });

  readonly state = connectState({
    markCalculationMethod: this.syllabusQuery.markCalculationMethod$,
  });

  private parseControlErrors(errors: ValidationErrors | null) {
    if (!errors) {
      return null;
    }

    const errorsArray = [];

    if (errors['invalidJson']) {
      errorsArray.push(errors['invalidJson'] as string);
    }

    if (errors['monacoValidation']) {
      errorsArray.push(
        ...(errors['monacoValidation'] as monacoEditor.IMarker[]).map(
          (marker) => `${marker.message}, line: ${marker.startLineNumber}`,
        ),
      );
    }

    return errorsArray.length ? errorsArray : null;
  }
}
